import React from 'react';
import styled, { css } from 'styled-components';
import { colors, spacer, rgba, radius, fontWeight } from 'settings/style';
import { ResizeImageResult } from 'helpers';
import { Button, ImageInput } from 'components/elements';
import { ImageInputAction } from 'components/elements/imageInput';
import AddCircle from 'assets/images/add-circle.svg';
import GroupCover1 from 'assets/images/group-cover-1.jpg';
import GroupCover2 from 'assets/images/group-cover-2.jpg';
import GroupCover3 from 'assets/images/group-cover-3.jpg';
import GroupCover4 from 'assets/images/group-cover-4.webp';
import GroupCover5 from 'assets/images/group-cover-5.jpg';

const DEFAULT_COVER_IMAGES = [
  GroupCover1,
  GroupCover2,
  GroupCover3,
  GroupCover4,
  GroupCover5,
];

interface CoverPhotoProps {
  onChange?: ImageInputAction;
  value?: ResizeImageResult;
}

function CoverPhoto({ onChange = () => { }, value }: CoverPhotoProps) {
  const selectedDefaultIndex = value?.url
    ? DEFAULT_COVER_IMAGES.findIndex((imageUrl) => imageUrl === value.url)
    : -1;
  const isUsingDefault = selectedDefaultIndex >= 0 && !value?.blob;

  const handleSelectDefault = async (index: number) => {
    const selectedUrl = DEFAULT_COVER_IMAGES[index];
    try {
      // Convert static/default image URL to a real File so backend upload gets blob content.
      const response = await fetch(selectedUrl);
      const blob = await response.blob();
      const extension = selectedUrl.split('.').pop() || 'jpg';
      const file = new File([blob], `default-cover-${index + 1}.${extension}`, {
        type: blob.type || 'image/jpeg',
      });

      onChange({
        url: selectedUrl,
        blob: file,
      } as ResizeImageResult);
    } catch (err) {
      // Fallback to URL-only if conversion fails; existing flows can still display preview.
      onChange({
        url: selectedUrl,
        blob: null,
      } as ResizeImageResult);
    }
  };

  const handleClearDefault = () => {
    onChange({} as ResizeImageResult);
  };

  const displayUrl = value?.url;

  return (
    <CoverPhotoContainer>
      <div>
        <SectionTitle>To upload a new photo, hover over the image then click “Change photo”</SectionTitle>
        <ImageInput width={900} height={600} cropping onChange={onChange} withEditor>
          <CoverPhotoWrapper imageUrl={displayUrl}>
            {!displayUrl && (
              <CoverPhotoEmptyContent>
                <img src={AddCircle} alt="" />
                <Button onClick={() => { }} type="primary" ghost>
                  Add cover photo
                </Button>
              </CoverPhotoEmptyContent>
            )}
          </CoverPhotoWrapper>
        </ImageInput>
      </div>

      <SectionTitle>Or Select Default Group Cover</SectionTitle>
      <DefaultImagesGrid>
        {DEFAULT_COVER_IMAGES.map((imageUrl, index) => (
          <DefaultImageOption
            key={index}
            isSelected={isUsingDefault && selectedDefaultIndex === index}
            onClick={() => handleSelectDefault(index)}
          >
            <DefaultImagePreview src={imageUrl} alt={`Default cover ${index + 1}`} />
            {isUsingDefault && selectedDefaultIndex === index && <SelectedBadge>✓ Selected</SelectedBadge>}
          </DefaultImageOption>
        ))}
      </DefaultImagesGrid>

      {isUsingDefault && (
        <Button type="default" onClick={handleClearDefault} style={{ marginTop: spacer.md }}>
          Clear Default Selection
        </Button>
      )}
    </CoverPhotoContainer>
  );
}


export default CoverPhoto;

const CoverPhotoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacer.lg};
`;

const SectionTitle = styled.h3`
  font-weight: ${fontWeight.bold};
  margin-bottom: ${spacer.md};
  color: ${colors.gray900};
`;

const CoverPhotoWrapper = styled.div<{ imageUrl?: string }>`
  width: 60%;
  outline: none;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  border-radius: ${radius.lg};
  cursor: pointer;
  ${({ imageUrl }) =>
    imageUrl
      ? css`
          background-image: url(${imageUrl});
          background-position: center;
          background-repeat: no-repeat;
          background-size: cover;
          position: relative;
          &:hover:before {
            content: 'Change Photo';
            position: absolute;
            display: block;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: ${rgba(colors.black, 0.5)};
            color: ${colors.white};
            font-weight: ${fontWeight.bold};
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: ${radius.lg};
          }
        `
      : css`
          border: 3px dashed ${colors.primary};
        `}

  &:after {
    content: '';
    display: block;
    padding-bottom: 66.6666%;
  }
`;

const CoverPhotoEmptyContent = styled.div`
  text-align: center;
  img {
    width: 150px;
    margin-bottom: ${spacer.lg};
  }
`;

const DefaultImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacer.md};
  margin-bottom: ${spacer.lg};
`;

const DefaultImageOption = styled.div<{ isSelected: boolean }>`
  position: relative;
  border-radius: ${radius.md};
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${({ isSelected }) => (isSelected ? colors.primary : colors.gray300)};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
    transform: scale(1.05);
  }
`;

const DefaultImagePreview = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${rgba(colors.primary, 0.9)};
  color: ${colors.white};
  padding: ${spacer.sm} ${spacer.md};
  border-radius: ${radius.md};
  font-weight: ${fontWeight.bold};
  font-size: 14px;
`;